






































































































































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import store from "@/store";
import _ from "underscore";
import autoGranularity from "Utilities/auto-granularity";
import moment from "moment";

//  @components
import FeatureNotAvailable from "@/components/FeatureNotAvailable.vue";
import PerformanceIndicatorChart from "@/components/PerformanceIndicatorChart.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import VesselEventModal from "@/components/VesselEventModal.vue";
import ManageEventsModal from "@/components/ManageEventsModal.vue";

//  import { ISO19030Event } from "@/types/ISO19030Event.js";
import { ISO19030TriggerValue } from "@/types/ISO19030TriggerValue.js";
import { PerformanceIndicatorPeriod } from "@/types/PerformanceIndicatorPeriod.js";
import { LogVariable } from "@/types/logVariable.js";
import { LogData } from "@/types/logData.js";
import { ExtendedVessel } from "@/types/vessel";
import { VesselEvent } from "@/types/vesselEvent";

//  modules
import { getModule } from "vuex-module-decorators";
import LogVariablesModule from "@/store/clients/LogVariables.module";
import ISO19030Module from "@/store/clients/ISO19030.module";
import LogDataClient from "Clients/log-data-client";
import VesselsModule from "@/store/clients/Vessels.module";
import SnackbarModule from "@/store/clients/Snackbar.module";
import VesselEventsModule from "@/store/clients/VesselEvents.module";
import LongTrendsModule from "@/store/clients/LongTrends.module";

//  types

const ISO19030Client = getModule(ISO19030Module, store);
const LogVariables = getModule(LogVariablesModule, store);
const Vessels = getModule(VesselsModule, store);
const Snackbar = getModule(SnackbarModule, store);
const VesselEvents = getModule(VesselEventsModule, store);
const LongTrends = getModule(LongTrendsModule, store);

@Component({
  components: {
    FeatureNotAvailable,
    ConfirmDialog,
    PerformanceIndicatorChart,
    ManageEventsModal,
    VesselEventModal,
  },
})
export default class ISO19030 extends Vue {
  @Ref("VesselEventModal") VesselEventModal!: any;
  @Ref("ConfirmDelete") ConfirmDelete!: any;
  @Ref("editVessel") editVessel!: any;
  @Ref("filterModal") filterModal!: any;

  // @Data
  isMaintenanceTriggerModalActive = false;
  ManageEventsModal = false;
  isVesselEventModal = false;
  isLoading = false;
  performanceIndicatorsLogVariable: LogVariable = {} as LogVariable;
  ISO19030PerformanceLogData: LogData = {} as LogData;
  ISOChartTypes = ["DryDocking Performance", "InService Performance", "Maintenance Trigger", "Maintenance Effect"];
  ISOChartDescriptions = [
    "Overall improvement in hull and propeller efficiency after most recent drydocking event, based on ISO 19030 methodology.",
    "Tracks how hull and propeller performance change during regular vessel's operation (service), compared to most recent post-drydocking condition.",
    "Trigger underwater hull and propeller maintenance, including propeller and/or hull inspection. Change in hull and propeller performance from the start of the dry-docking interval (Reference period) to a moving average of the last 30 days (evaluation period).",
    "Maintenance Effect measures how much a maintenance event improved performance, by comparing the vessel’s condition before and after the maintenance event.",
  ];
  maintenanceTriggerValue: string | null = null;
  maintenanceTriggerValueDraft: string | null = null;
  EditEventOpen = false;
  hasFilterUpdated = false;
  showSpeedLogDeviation = true;

  // @Watchers
  @Watch("isMaintenanceTriggerModalActive")
  onMaintenanceDialogToggle(newVal: boolean): void {
    if (!newVal) {
      // dialog closed without saving -> discard draft
      this.maintenanceTriggerValueDraft = null;
    } else {
      // dialog opened -> initialize draft
      this.initMaintenanceTriggerDraft();
    }
  }

  @Watch("ISO19030PerformanceLogVariable")
  async onISO19030PerformanceLogVariableChange(): Promise<void> {
    this.ISO19030PerformanceLogData = {} as LogData;
    await this.initViewData();
  }

  //  @Getters
  get vessel(): ExtendedVessel | null {
    if (!Vessels.currentVessel) return null;
    return Vessels.currentVessel;
  }

  get isIso19030Enabled(): boolean {
    if (!this.vessel) return false;
    return this.vessel.features.some(feature => feature.name === "Iso19030");
  }

  get vesselId(): number {
    return Number(this.$route.params.vesselId);
  }

  get performanceIndicators(): PerformanceIndicatorPeriod[] {
    return ISO19030Client.performanceIndicators;
  }
  get events(): VesselEvent[] {
    return VesselEvents.allEvents;
  }

  get filteredEvents(): VesselEvent[] {
    // Filter events to only show those after logDataMinDate
    const minDate = this.ISO19030PerformanceLogVariable?.logDataMinDate;
    if (!minDate) return this.events;

    const minDateTimestamp = moment.utc(minDate).valueOf();
    return this.events.filter(event => {
      const eventTimestamp = moment.utc(event.timestamp).valueOf();
      return eventTimestamp >= minDateTimestamp;
    });
  }

  /**
   * Public getter used by template / child components.
   * If a cached previous trigger value exists and is still valid (24h),
   * return that cached previous value (i.e. continue using the old value).
   * Otherwise return the persisted/current maintenanceTriggerValue (API).
   */
  get maintenanceTriggerValueEffective(): string | null {
    const vesselId = this.vesselId || 0;
    const cache = this.getTriggerPreviousCache(vesselId);
    if (cache && cache.previousValue !== undefined) {
      return cache.previousValue;
    }
    return this.maintenanceTriggerValue;
  }

  get maintenanceTriggerValueIsUndefined(): boolean {
    return this.maintenanceTriggerValue === undefined || this.maintenanceTriggerValue === null || this.maintenanceTriggerValue === "undefined";
  }

  get maintenanceTriggerValueInvalid(): boolean {
    const raw = this.isMaintenanceTriggerModalActive ? this.maintenanceTriggerValueDraft : this.maintenanceTriggerValue;
    if (raw === undefined || raw === null) return true;
    return String(raw).trim() === "";
  }

  get maintenanceTriggerValueDisplay(): string {
    return this.maintenanceTriggerValueIsUndefined ? "" : String(this.maintenanceTriggerValue);
  }

  get ISO19030PerformanceLogVariable(): LogVariable {
    return LogVariables.logVariableISO19030PerformanceValue;
  }

  //  @Methods
  initMaintenanceTriggerDraft(): void {
    if (this.maintenanceTriggerValueDraft === null) {
      this.maintenanceTriggerValueDraft = this.maintenanceTriggerValueIsUndefined ? "" : String(this.maintenanceTriggerValue);
    }
  }

  onMaintenanceTriggerDraftInput(val: string): void {
    this.maintenanceTriggerValueDraft = val;
  }

  onVesselEventUpdate(): void {
    Snackbar.showSnackbar({ text: `Events successfully updated`, color: "ContentPrimaryStatic" });
  }

  async deleteEvent(vesselEvent: VesselEvent): Promise<void> {
    const confirmed = await this.ConfirmDelete.open(`Delete Event`, `Are you sure you want to delete <b>${vesselEvent.name}</b> event?`);
    if (confirmed) {
      await VesselEvents.deleteEvent(vesselEvent);
      Snackbar.showSnackbar({ text: `Event ${vesselEvent.name} successfully deleted`, color: "ContentPrimaryStatic" });
    }
  }

  // --- Cache helpers for previous trigger value (kept for 24h) ---
  getTriggerCacheKey(vesselId: number): string {
    return `iso19030.trigger.previous.${vesselId}`;
  }

  setTriggerPreviousCache(vesselId: number, previousValue: string | null): void {
    try {
      const key = this.getTriggerCacheKey(vesselId);
      const payload = {
        previousValue,
        expiresAt: Date.now() + 24 * 60 * 60 * 1000, // 24 hours
      };
      localStorage.setItem(key, JSON.stringify(payload));
    } catch (e) {
      // ignore storage errors
      // console.warn("Failed to set trigger cache", e);
    }
  }

  getTriggerPreviousCache(vesselId: number): { previousValue: string | null; expiresAt: number } | null {
    try {
      const key = this.getTriggerCacheKey(vesselId);
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.expiresAt) {
        localStorage.removeItem(key);
        return null;
      }
      if (Number(parsed.expiresAt) > Date.now()) return parsed;
      // expired
      localStorage.removeItem(key);
      return null;
    } catch (e) {
      // parse error or access denied
      try {
        localStorage.removeItem(this.getTriggerCacheKey(vesselId));
      } catch {}
      return null;
    }
  }

  clearTriggerPreviousCache(vesselId: number): void {
    try {
      localStorage.removeItem(this.getTriggerCacheKey(vesselId));
    } catch {}
  }

  async editMaintenanceTrigger(): Promise<void> {
    this.initMaintenanceTriggerDraft();
    await ISO19030Client.fetchPerformanceIndicators(this.vesselId);
    await this.fetchPerformanceIndicatorsData();
    this.isMaintenanceTriggerModalActive = true;
  }

  numericFilter(event: KeyboardEvent): void {
    event = event || window.event;
    const key = (event as KeyboardEvent).key;

    // allow control keys (Backspace, Tab, Enter, Arrow keys, Delete, etc.)
    if (key.length !== 1) return;

    // allow one dot only
    if (key === ".") {
      const input = event.target as HTMLInputElement;
      if (input && input.value && input.value.includes(".")) {
        event.preventDefault();
      }
      return;
    }

    // allow digits 0-9 only
    if (!/^\d$/.test(key)) {
      event.preventDefault();
    }
  }

  getISOChartTypePeriods(type: string): PerformanceIndicatorPeriod[] {
    switch (type) {
      case "DryDocking Performance":
        return this.performanceIndicators.filter(item => item.chartName === "DryDocking Performance");
      case "InService Performance":
        return this.performanceIndicators.filter(item => item.chartName === "InService Performance");
      case "Maintenance Trigger":
        return this.performanceIndicators.filter(item => item.chartName === "Maintenance Trigger");
      case "Maintenance Effect":
        return this.performanceIndicators.filter(item => item.chartName === "Maintenance Effect");
      default:
        return [] as PerformanceIndicatorPeriod[];
    }
  }

  async fetchPerformanceIndicatorsData(): Promise<void> {
    if (_.isEmpty(this.ISO19030PerformanceLogVariable)) {
      this.ISO19030PerformanceLogData = {} as LogData;
      return;
    }
    const minDate = this.ISO19030PerformanceLogVariable.logDataMinDate ?? "";
    const maxDate = this.ISO19030PerformanceLogVariable.logDataMaxDate ?? "";
    const selectedTimeInMinutes = this.minutesDiff(new Date(minDate), new Date(maxDate));

    this.ISO19030PerformanceLogData = await LogDataClient.findLogData(this.ISO19030PerformanceLogVariable.id, minDate, maxDate, autoGranularity(selectedTimeInMinutes));
  }

  minutesDiff(fromDate: Date, toDate: Date): number {
    const diff = (fromDate.getTime() - toDate.getTime()) / 1000 / 60;

    return Math.abs(Math.round(diff));
  }

  async initViewData(): Promise<void> {
    if (!this.vessel) return;

    try {
      this.isLoading = true;
      await Promise.all([
        ISO19030Client.fetchPerformanceIndicators(this.vesselId),
        this.fetchPerformanceIndicatorsData(),
        VesselEvents.fetchEvents(this.vessel.id),
        VesselEvents.updateTrendEventTypes(),
      ]);

      // Fetch the Maintenance Trigger Value
      const triggerValue = await ISO19030Client.getTriggerValue(this.vesselId);
      this.maintenanceTriggerValue = triggerValue !== null ? String(triggerValue) : null;
    } catch (error) {
      console.warn(error);
    } finally {
      this.isLoading = false;
    }
  }

  async saveMaintenanceTriggerValue(): Promise<void> {
    try {
      if (!this.vessel) return;

      // keep previous value in cache for 24 hours so UI / charts keep using it
      const previous = this.maintenanceTriggerValue === undefined ? null : this.maintenanceTriggerValue;

      const raw = this.maintenanceTriggerValueDraft !== null ? this.maintenanceTriggerValueDraft : this.maintenanceTriggerValue;
      const normalizedValue = typeof raw === "string" ? raw.replace(",", ".") : raw;
      const triggerValue = normalizedValue !== null && normalizedValue !== "" ? Number(normalizedValue) : null;

      await ISO19030Client.setTriggerValue({ vesselId: this.vessel.id, triggerValue } as ISO19030TriggerValue);
      await this.fetchPerformanceIndicatorsData();

      // persist only after successful save
      this.maintenanceTriggerValue = triggerValue !== null ? String(triggerValue) : null;
      this.maintenanceTriggerValueDraft = null;

      // store the previous value in cache for 24h so UI continues to use previous until expiry
      this.setTriggerPreviousCache(this.vessel.id, previous);

      Snackbar.showSnackbar({ text: "Maintenance Trigger Value updated successfully!", color: "ContentPrimaryStatic" });
      if (this.isMaintenanceTriggerModalActive) this.isMaintenanceTriggerModalActive = false;
    } catch (error) {
      console.error(error);
      Snackbar.showSnackbar({ text: "Failed to update Maintenance Trigger Value", color: "error" });
    }
  }

  // @Hooks
  async created(): Promise<void> {
    await this.initViewData();
  }
}
